<script setup lang="ts">
import debounce from 'lodash/debounce'

import Icon from '~/components/common/Icon.vue'
import UserOptions from '~/components/UserOptions.vue'

import ClientDebounce from '~/utils/client-debounce'
import { useTicker } from '~/composables/useTicker'
import { useTickerAnimation } from '~/composables/useTickerAnimation'

import useAuthentication from '~/composables/useAuthentication'
import type { TTickerData } from '~/server/types/ticker'

const { authenticationChanged } = useAuthentication()

const { indexes: tickerIndexes, refreshTickerData } = useTicker()

const {
  startAnimation,
  resumeAnimation,
  pauseAnimation,
  moveToLeft,
  moveToRight,
  cleanupAnimation,
} = useTickerAnimation()

const isFirstIndexesContainerMounted = ref(false)
const isSecondIndexesContainerMounted = ref(false)
const debounceResumeAnimation = new ClientDebounce()
const indexes = computed<TTickerData>(() => tickerIndexes.value)

const resumeAnimationDelay = debounce(() => {
  if (import.meta.server) return
  resumeAnimation()
}, 1000)

const handlePauseAnimation = () => {
  pauseAnimation()
  debounceResumeAnimation.clear()
}

const handleMoveToLeft = () => {
  moveToLeft()
  resumeAnimationDelay()
}

const handleMoveToRight = () => {
  moveToRight()
  resumeAnimationDelay()
}

watch(
  [isFirstIndexesContainerMounted, isSecondIndexesContainerMounted, indexes],
  ([first, second, currentIndexes]) => {
    if (import.meta.server) return
    if (!first || !second || !currentIndexes || currentIndexes.length === 0)
      return
    nextTick(() => {
      startAnimation()
    })
  },
)

watch(authenticationChanged, async () => {
  if (import.meta.server) return
  await refreshTickerData(true)
})

onMounted(() => {
  if (
    isFirstIndexesContainerMounted.value &&
    isSecondIndexesContainerMounted.value &&
    indexes.value &&
    indexes.value.length > 0
  ) {
    nextTick(() => {
      startAnimation()
    })
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    cleanupAnimation()
    debounceResumeAnimation.clear()
  }
})
</script>

<template>
  <div
    class="bg-s-900 text-white flex items-center justify-center w-full py-2 px-0 md:px-4 lg:pl-10 lg:pr-6 2xl:px-0"
  >
    <div
      class="max-w-content-container relative flex items-center justify-between gap-2 flex-1"
    >
      <div
        class="relative flex h-5 min-w-0 grow transition-opacity text-sm opacity-100"
      >
        <button
          class="flex w-5 items-center justify-center"
          @click="handleMoveToRight"
        >
          <Icon name="angle-left" />
        </button>
        <div
          id="ticker-container"
          class="relative flex grow overflow-hidden scroll-smooth pr-16 font-semibold"
          @mouseover="handlePauseAnimation"
          @mouseleave="resumeAnimationDelay"
        >
          <div class="indexes-container absolute flex items-center gap-8 pr-8">
            <TickerIndex
              v-for="index in indexes"
              :key="index.name"
              :index="index"
              @resume-animation="resumeAnimationDelay"
              @pause-animation="handlePauseAnimation"
              @mounted="isFirstIndexesContainerMounted = true"
            />
          </div>
          <div class="indexes-container absolute flex items-center gap-8 pr-8">
            <TickerIndex
              v-for="index in indexes"
              :key="index.name"
              :index="index"
              @resume-animation="resumeAnimation"
              @pause-animation="handlePauseAnimation"
              @mounted="isSecondIndexesContainerMounted = true"
            />
          </div>
        </div>

        <button
          class="flex w-5 items-center justify-center"
          @click="handleMoveToLeft"
        >
          <Icon name="RightAngle" />
        </button>
      </div>
      <div class="flex items-center justify-between gap-x-5">
        <NuxtLink
          to="/lithium-ion-battery-raw-material-index"
          class="flex items-center gap-2"
        >
          <Icon name="Curves" class="h-3 w-3 xl:h-4 xl:w-4" />
          <span class="text-sm"> Lithium ion Battery Raw Material Index </span>
        </NuxtLink>
        <UserOptions class="hidden lg:block" />
      </div>
    </div>
  </div>
</template>
